import { cx } from './cx'

export type ProgressTone = 'brand' | 'positive' | 'warning' | 'critical'

const FILL: Record<ProgressTone, string> = {
  brand: 'bg-brand-600',
  positive: 'bg-emerald-600',
  warning: 'bg-amber-500',
  critical: 'bg-red-600',
}

/**
 * A horizontal progress bar, determinate or not.
 *
 * The distinction is the point of this component rather than a detail of it.
 * Not every sync can be measured: Clio reports how many records match a
 * filtered collection on some endpoints and not others, so a run may genuinely
 * not know its own size until it ends. Rendering a made-up percentage in that
 * case — reading a partial count against last night's total, say — would put a
 * confident number on screen that is wrong, on the one screen whose job is
 * saying how much to trust the data. So an unmeasurable run gets a moving bar
 * with no percentage, and the count beside it carries the real information.
 */
export function Progress({
  value,
  max,
  tone = 'brand',
  label,
  className,
}: {
  value: number
  /** Omit — or pass a non-positive total — for an indeterminate bar. */
  max?: number | null
  tone?: ProgressTone
  /** Accessible name; the visible caption lives next to the bar, not in it. */
  label: string
  className?: string
}) {
  const determinate = typeof max === 'number' && max > 0

  /**
   * Clamped, because a total is a claim and the count is a fact. Clio's total
   * is computed when the first page is served, so a collection that grows
   * mid-pull can legitimately read 104% — and a bar overflowing its track looks
   * like a rendering bug rather than the honest thing it is.
   */
  const percent = determinate ? Math.min(100, Math.round((value / max!) * 100)) : null

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      {...(determinate
        ? { 'aria-valuemax': max!, 'aria-valuenow': value, 'aria-valuetext': `${percent}%` }
        : // No `aria-valuenow` is how ARIA spells "indeterminate".
          {})}
      className={cx(
        'h-1.5 w-full overflow-hidden rounded-full bg-ink-200',
        !determinate && 'sync-indeterminate',
        className
      )}
    >
      {determinate && (
        <div
          className={cx('h-full rounded-full transition-[width] duration-500 ease-out', FILL[tone])}
          style={{ width: `${percent}%` }}
        />
      )}
    </div>
  )
}
